'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { generateFromString } from 'generate-avatar'
import { MessageSquareQuote, Star } from 'lucide-react'

import type { Testimonial } from '@/lib/content/testimonials'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { MediaImage as Image } from '@/components/media-image'
import { PunkTab } from '@/components/punk-tab'
import { ReviewLightbox } from '@/components/review-lightbox'
import { ScrollReveal } from '@/components/scroll-reveal'
import { Sparkles } from '@/components/sparkles'

// Lazy-load the feedback form. Splits react-hook-form + altcha + zod
// out of the home-page bundle; the gate is below the fold and only
// opens on user interaction.
const ReviewGate = dynamic(() => import('@/components/review-gate').then((m) => m.ReviewGate))

const GLOW_CYCLE = [
  'shadow-[0_0_12px_rgba(43,162,42,0.45)]',
  'shadow-[0_0_12px_rgba(135,42,135,0.45)]',
  'shadow-[0_0_12px_rgba(149,248,98,0.4)]',
]

interface ReviewGalleryProps {
  testimonials: Testimonial[]
}

export function ReviewGallery({ testimonials }: ReviewGalleryProps) {
  const [lightbox, setLightbox] = useState<{
    photos: { src: string; alt: string }[]
    index: number
  } | null>(null)

  if (testimonials.length === 0) return null

  const featuredIndex = Math.max(
    0,
    testimonials.findIndex((t) => t.featured)
  )
  const featured = testimonials[featuredIndex]
  // Carousel reviews: image-having ones first so the visual proof leads the
  // sequence. Stable order within each bucket (preserves the data ordering).
  const others = testimonials
    .filter((_, i) => i !== featuredIndex)
    .map((t, i) => ({ t, i }))
    .sort((a, b) => {
      const ap = a.t.photos.length > 0 ? 0 : 1
      const bp = b.t.photos.length > 0 ? 0 : 1
      return ap - bp || a.i - b.i
    })
    .map(({ t }) => t)
  const featuredPhotos = featured.photos

  return (
    <>
      <h2 className="font-display flex items-center gap-3 text-4xl font-bold sm:text-5xl">
        <MessageSquareQuote
          className="text-brand-purple size-10 shrink-0 sm:size-12"
          aria-hidden="true"
        />
        What The Neighbours Reckon
      </h2>
      <p className="mt-3 text-lg text-[#555]">
        Real jobs from around the hills. Driveways, walls, the lot.
      </p>

      <ScrollReveal direction="up">
        <blockquote
          className={`relative mt-8 grid gap-5 rounded-sm border border-[#e5e0ea] bg-white p-6 pt-8 shadow-sm sm:p-8 ${
            featuredPhotos.length > 0 ? 'sm:grid-cols-[3fr_2fr]' : ''
          }`}
        >
          <PunkTab colour="purple" rotation="-9deg" size="lg">
            &ldquo;
          </PunkTab>
          <Sparkles seed={13} />
          <div className="flex flex-col">
            <div className="mb-3 flex gap-1">
              {Array.from({ length: featured.rating }).map((_, i) => (
                <Star
                  key={i}
                  className="text-brand-chartreuse-vivid size-5 fill-current"
                  aria-hidden="true"
                />
              ))}
            </div>
            <p className="flex-1 text-lg leading-relaxed text-[#2a2a2a] sm:text-xl">
              &ldquo;{featured.quote}&rdquo;
            </p>
            <footer className="mt-5 flex items-center gap-3">
              <Image
                src={`data:image/svg+xml;utf8,${generateFromString(`${featured.author} ${featured.location}`)}`}
                alt=""
                width={44}
                height={44}
                className="size-11 rounded-full shadow-[0_0_12px_rgba(135,42,135,0.45)]"
              />
              <div>
                <span className="font-heading text-brand-purple text-lg font-semibold">
                  {featured.author}
                </span>
                <span className="block text-sm text-[#777]">{featured.location}, VIC</span>
              </div>
            </footer>
          </div>
          {featuredPhotos.length > 0 && (
            <div
              className={`grid gap-2 ${
                featuredPhotos.length === 1
                  ? 'grid-cols-1'
                  : featuredPhotos.length <= 4
                    ? 'grid-cols-2'
                    : 'grid-cols-3'
              }`}
            >
              {featuredPhotos.map((photo, i) => (
                <button
                  key={photo.src}
                  type="button"
                  onClick={() => setLightbox({ photos: featuredPhotos, index: i })}
                  className="group relative aspect-[4/3] overflow-hidden rounded-sm bg-[#ece4f0]"
                >
                  <Image
                    src={photo.src}
                    alt={photo.alt}
                    fill
                    sizes="(min-width: 1024px) 384px, (min-width: 640px) 256px, 50vw"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          )}
        </blockquote>
      </ScrollReveal>

      {others.length > 0 && (
        <Carousel
          opts={{ align: 'start', loop: false }}
          className="mt-8"
          aria-label="Customer reviews"
        >
          <CarouselContent className="-ml-4 sm:-ml-6">
            {others.map((testimonial, index) => {
              const photos = testimonial.photos
              const glow = GLOW_CYCLE[index % GLOW_CYCLE.length]
              return (
                <CarouselItem
                  key={`${testimonial.author}-${index}`}
                  className="basis-full pl-4 sm:basis-1/2 sm:pl-6 lg:basis-1/3"
                >
                  <blockquote className="flex h-full flex-col rounded-sm border border-[#d8cfe0] bg-white p-7 shadow-sm sm:p-8">
                    <div className="mb-4 flex gap-1">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <Star
                          key={i}
                          className="text-brand-chartreuse-vivid size-5 fill-current"
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                    <p className="flex-1 text-base leading-relaxed text-[#3a3a3a]">
                      &ldquo;{testimonial.quote}&rdquo;
                    </p>
                    {photos.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 gap-1.5">
                        {photos.map((photo, photoIdx) => (
                          <button
                            key={photo.src}
                            type="button"
                            onClick={() => setLightbox({ photos, index: photoIdx })}
                            className="group relative aspect-[3/2] overflow-hidden rounded-sm bg-[#ece4f0]"
                          >
                            <Image
                              src={photo.src}
                              alt={photo.alt}
                              fill
                              sizes="(min-width: 1024px) 120px, (min-width: 640px) 160px, 33vw"
                              className="object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                    <footer className="mt-5 flex items-center gap-3 border-t border-[#eee] pt-4">
                      <Image
                        src={`data:image/svg+xml;utf8,${generateFromString(`${testimonial.author} ${testimonial.location} ${index}`)}`}
                        alt=""
                        width={40}
                        height={40}
                        className={`size-10 rounded-full ${glow}`}
                      />
                      <div>
                        <span className="font-heading text-brand-purple text-base font-semibold">
                          {testimonial.author}
                        </span>
                        <span className="block text-sm text-[#777]">
                          {testimonial.location}, VIC
                        </span>
                      </div>
                    </footer>
                  </blockquote>
                </CarouselItem>
              )
            })}
          </CarouselContent>
          <CarouselPrevious className="hidden sm:-left-4 sm:flex lg:-left-6" />
          <CarouselNext className="hidden sm:-right-4 sm:flex lg:-right-6" />
        </Carousel>
      )}

      <ScrollReveal direction="up" delay={200}>
        <div className="mt-10">
          <ReviewGate />
        </div>
      </ScrollReveal>

      {lightbox && (
        <ReviewLightbox
          photos={lightbox.photos}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  )
}
